import { giphyBlue, giphyDarkestGrey, giphyDarkGrey, giphyLightCharcoal, giphyYellow } from '@giphy/js-brand'
import { Button } from '@giphy/react-giphy-brand'
import { first, floor, last, reduce } from 'lodash'
import React, { useContext, useEffect, useState } from 'react'
import MessageContext from 'shared/components/context/message'
import DotsLoader from 'shared/components/loaders/dots'
import { googleAnalyticsEvent } from 'shared/util/analytics'
import styled from 'styled-components'
import { ICapture, IEditParams, ISegment } from '../shared/types'
import Imaging from '../shared/utils/imaging'
import { getDefaultEditParams } from '../shared/utils/utils'

const MAX_ITEMS = 3
const ITEMS_PER_PAGE = 4
const PREVIEW_WIDTH = 375

const Container = styled.div`
    padding: 25px;
`

const Heading = styled.div`
    width: 100%;
    margin-bottom: 15px;
    border-bottom: 1px solid ${giphyDarkGrey};
`

const Tab = styled.div`
    font-family: nexablack;
    display: inline-block;
    margin-right: 25px;
    font-size: 16px;
    cursor: pointer;
    height: 35px;
    border-bottom: 4px solid ${giphyBlue};
`

const Content = styled.div``

const Flex = styled.div`
    display: flex;
    justify-content: space-between;
`

const Left = styled.div`
    width: 50%;
    padding-right: 10px;
    position: relative;
`

const Right = styled.div`
    width: 50%;
    padding-left: 10px;
`

const Actions = styled.div`
    margin: 100px auto 0 auto;
    width: 500px;
`

const SaveButton = styled(Button)`
    display: block;
    width: 100%;
    margin-bottom: 10px;
`

const CancelButton = styled(SaveButton)`
    background-color: ${giphyDarkestGrey};
`

const NextPrevButton = styled.div`
    background-color: ${giphyLightCharcoal};
    color: white;
    height: 22px;
    width: 36px;
    display: inline-block;
    text-align: center;
    line-height: 25px;
    font-size: 12px;
    cursor: pointer;
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 8px;
    row-gap: 8px;
`

const GridCell = styled.div`
    width: 100%;
    position: relative;
`

const SelectedBorder = styled.div`
    border: 6px solid ${giphyBlue};
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
`

const SelectedNumber = styled.div`
    font-family: NexaBlack;
    position: absolute;
    top: 0;
    right: 0;
    width: 28px;
    height: 28px;
    background-color: ${giphyYellow};
    color: ${giphyDarkestGrey};
    font-size: 16px;
    line-height: 28px;
    text-align: center;
    pointer-events: none;
`

const Thumbnail = styled.img`
    width: 100%;
    display: block;
`

const PreviewVideo = styled.video`
    width: 100%;
    display: block;
`

const Choose2 = styled.div`
    border: 3px dashed ${giphyLightCharcoal};
    text-align: center;
    height: 190px;
    padding: 75px;
`

const Instructions = styled.div`
    font-size: 17px;
    height: 40px;
    margin: 30px 0;
`

const Paging = styled.div``

const Title = styled.div`
    font-weight: bold;
    height: 23px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
`

const PreviewLoader = styled(DotsLoader)`
    z-index: -1;
`

type Props = {
    capture: ICapture
    segments: ISegment[]
    onCancel?: () => void
    onSave?: (segment: ISegment) => void
    soundEnabled?: boolean
    initialSelection: number[]
}

const Merge = ({
    capture,
    segments,
    onCancel = () => {},
    onSave = () => {},
    soundEnabled = false,
    initialSelection = [],
}: Props) => {
    const { sendMessage } = useContext(MessageContext)

    const [page, setPage] = useState<number>(0)
    const [selected, setSelected] = useState<number[]>([])

    useEffect(() => {
        if (initialSelection.length > 0) {
            setSelected(initialSelection)
            setPage(floor(last(initialSelection)! / ITEMS_PER_PAGE))
        }
    }, [])

    const saveClicked = () => {
        googleAnalyticsEvent({
            category: 'Super Cut',
            action: 'Merge',
            label: capture.id,
        })
        const mergedSegment = getMergedSegment()
        sendMessage('Merge Successful!')
        onSave(mergedSegment)
    }

    const cancelClicked = () => {
        onCancel()
    }

    const thumbnailClicked = index => {
        const foundIndex = selected.indexOf(index)
        // if it's not selected, add it, otherwise remove it from the selected list.
        if (foundIndex === -1) {
            if (selected.length < MAX_ITEMS) {
                selected.push(index)
            } else {
                sendMessage(`Only up to ${MAX_ITEMS} clips can be merged`, 'error')
            }
        } else {
            selected.splice(foundIndex, 1)
        }

        setSelected([...selected])
    }

    const getOrderedSegments = () => {
        // This reverse() is in there because otherwise the gif gets merged backwards.
        // Easier to fix here than in imaging service.
        const sortedIndexes = selected
            .slice()
            .sort()
            .reverse()
        const orderedSegments = sortedIndexes.map(index => segments[index])
        return orderedSegments
    }

    const getMergedSegment = (width?: number) => {
        const orderedSegments = getOrderedSegments()
        const firstSegment = first(orderedSegments)!
        const lastSegment = last(orderedSegments)!
        const duration = reduce(orderedSegments, (sum, s) => sum + s.duration, 0)

        const mergedVideoUrl = Imaging.concatenate(
            orderedSegments.map(s => s.video_url),
            'mp4',
            width
        )
        const mergedPreviewUrl = Imaging.downsample(mergedVideoUrl, 5, 'gif')

        return {
            index: segments.length,
            start_time: firstSegment.start_time,
            end_time: lastSegment.end_time,
            duration: duration,
            thumb_url: firstSegment.thumb_url,
            video_url: mergedVideoUrl,
            preview_url: mergedPreviewUrl,
            caption: '',
            editParams: {
                ...getDefaultEditParams(capture, firstSegment),
                trimmedEndTime: duration,
                displayOrder: lastSegment.index,
                isMerged: true,
            } as IEditParams,
        } as ISegment
    }

    const nextClicked = () => {
        setPage(page + 1)
    }

    const prevClicked = () => {
        setPage(page - 1)
    }

    const offset = page * ITEMS_PER_PAGE

    return (
        <Container>
            <Heading>
                <Tab>Merge GIFs</Tab>
            </Heading>
            <Content>
                <Instructions>
                    Merging GIFs creates a new single GIF of your selections. <br />
                    Original GIFs remain untouched.
                </Instructions>
                <Flex>
                    <Left>
                        <Title>Preview</Title>
                        {selected.length >= 2 ? (
                            <>
                                <PreviewVideo
                                    src={getMergedSegment(PREVIEW_WIDTH).video_url}
                                    autoPlay={true}
                                    loop={true}
                                    muted={!soundEnabled}
                                />
                                <PreviewLoader isAbsolute />
                            </>
                        ) : (
                            <Choose2>Choose at least 2 GIFs on the right to merge.</Choose2>
                        )}
                    </Left>
                    <Right>
                        <Flex>
                            <Title>
                                Choose Your GIFs
                                <Paging>
                                    <NextPrevButton onClick={prevClicked}>
                                        <span className="ss-navigateleft" />
                                    </NextPrevButton>{' '}
                                    <NextPrevButton onClick={nextClicked}>
                                        <span className="ss-navigateright" />
                                    </NextPrevButton>
                                </Paging>
                            </Title>
                        </Flex>
                        <Grid>
                            {segments.slice(offset, offset + 4).map(s => {
                                const selectedIndex = selected.indexOf(s.index)
                                return (
                                    <GridCell key={s.index}>
                                        <Thumbnail src={s.preview_url} onClick={() => thumbnailClicked(s.index)} />
                                        {selectedIndex !== -1 && (
                                            <>
                                                <SelectedBorder />
                                                <SelectedNumber>{selectedIndex + 1}</SelectedNumber>
                                            </>
                                        )}
                                    </GridCell>
                                )
                            })}
                        </Grid>
                    </Right>
                </Flex>
            </Content>
            <Actions>
                <SaveButton onClick={saveClicked} disabled={selected.length < 2}>
                    Merge {selected.length} GIFs
                </SaveButton>
                <CancelButton onClick={cancelClicked}>Cancel</CancelButton>
            </Actions>
        </Container>
    )
}

export default Merge
